import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Upload, CheckCircle } from 'lucide-react';
import { uploadFile } from '../lib/upload';
import { PasswordInput } from '../components/PasswordInput';
import PhotoCropper from '../components/PhotoCropper';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function BecomeGuide() {
  const { t } = useTranslation();
  const { user, isAuthenticated, setUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pendingPhoto, setPendingPhoto] = useState(null);
  const [pendingSrc, setPendingSrc] = useState(null);   // HEIC déjà converti par le serveur
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const googleBtnRef = React.useRef(null);

  // Inscription guide via Google : même mécanisme que la page Login. Une fois
  // connecté (compte Google), le formulaire cache email/mot de passe et la
  // candidature part sur ce compte — devenir guide = poser le rôle "guide".
  function handleGoogleResponse(resp) {
    if (!resp || !resp.credential) return;
    axios.post(API_URL + '/api/auth/google', { credential: resp.credential }, { withCredentials: true })
      .then(function(res) { setUser(res.data); toast.success(t('becomeHost.googleConnected')); })
      .catch(function() { toast.error(t('becomeHost.googleError')); });
  }

  useEffect(function() {
    if (isAuthenticated || !GOOGLE_CLIENT_ID) return;
    function renderBtn() {
      if (!window.google || !window.google.accounts || !window.google.accounts.id) return;
      window.google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: handleGoogleResponse });
      if (googleBtnRef.current) {
        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'filled_black', size: 'large', shape: 'pill', text: 'continue_with', logo_alignment: 'center', width: 320
        });
      }
    }
    if (document.getElementById('google-gsi-script')) { renderBtn(); return; }
    var script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true; script.defer = true; script.id = 'google-gsi-script';
    script.onload = renderBtn;
    document.body.appendChild(script);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    bio_fr: '',
    bio_en: '',
    bio_jp: '',
    languages_spoken: [],
    language_levels: { en: 0, fr: 0, jp: 0 },
    specialties: [],
    areas: [],
    areas_text: '',
    id_document_url: '',
    profile_photo_url: '',
    profile_photo_original_url: '',
    profile_photo_crop: {},
    visa_status: '',
    agreement_accepted: false
  });

  const languageOptions = ['fr', 'en', 'jp', 'es', 'de', 'zh'];
  const specialtyOptions = ['food', 'culture', 'nightlife', 'shopping', 'nature'];

  const handleCheckbox = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleFileUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    // La photo de profil passe d'abord par le recadrage imposé (carré).
    if (field === 'profile_photo_url') {
      e.target.value = '';
      // Photo iPhone (HEIC/HEIF) : illisible par le navigateur, on la fait
      // convertir en JPEG par le serveur avant de la cadrer.
      const heic = /\.(heic|heif)$/i.test(file.name || '') || /heic|heif/i.test(file.type || '');
      if (heic) {
        setUploadingPhoto(true);
        try {
          setPendingSrc(await uploadFile(file));
        } catch (err) {
          toast.error(t('becomeHost.uploadError'));
        } finally {
          setUploadingPhoto(false);
        }
        return;
      }
      setPendingPhoto(file);
      return;
    }

    try {
      const url = await uploadFile(file);
      setFormData(prev => ({ ...prev, [field]: url }));
      toast.success(t('becomeHost.fileUploaded'));
    } catch (error) {
      toast.error(t('becomeHost.uploadError'));
    }
  };

  // On garde l'originale en plus du recadrage : le guide pourra revenir modifier
  // son cadrage depuis son espace, sans re-téléverser sa photo.
  const uploadCroppedPhoto = async (croppedFile, crop) => {
    setUploadingPhoto(true);
    try {
      const jobs = [uploadFile(croppedFile)];
      if (pendingPhoto) jobs.push(uploadFile(pendingPhoto));
      const urls = await Promise.all(jobs);
      setFormData(prev => ({
        ...prev,
        profile_photo_url: urls[0],
        profile_photo_original_url: urls[1] || pendingSrc || '',
        profile_photo_crop: crop
      }));
      setPendingPhoto(null);
      setPendingSrc(null);
      toast.success(t('becomeHost.fileUploaded'));
    } catch (error) {
      toast.error(t('becomeHost.uploadError'));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.visa_status) { toast.error(t('becomeHost.visaRequired')); return; }
    if (!formData.agreement_accepted) { toast.error(t('becomeHost.agreementRequired')); return; }

    setLoading(true);
    try {
      // Si pas connecté : créer le compte d'abord
      if (!isAuthenticated) {
        if (!formData.email || !formData.password) {
          toast.error('Email et mot de passe requis');
          setLoading(false);
          return;
        }
        const regRes = await axios.post(`${API_URL}/api/auth/register`, {
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name,
        }, { withCredentials: true });
        setUser(regRes.data);
      }

      const areasArray = formData.areas_text
        ? formData.areas_text.split(',').map(a => a.trim()).filter(Boolean)
        : formData.areas;
      await axios.post(
        `${API_URL}/api/guides/apply`,
        { ...formData, areas: areasArray },
        { withCredentials: true }
      );
      toast.success(t('becomeHost.applicationSuccess'));
      navigate('/guide/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('becomeHost.applicationError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-12" data-testid="become-guide-page">
      <div className="max-w-4xl mx-auto px-6">
        <div className="mb-12 text-center">
          <h1 
            className="text-4xl sm:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: 'Syne, sans-serif' }}
          >
            {t('becomeHost.title')}
          </h1>
          <p className="text-xl text-zinc-400 mb-6">{t('becomeHost.subtitle')}</p>
          
          {/* Legal Disclaimer */}
          <div className="max-w-2xl mx-auto bg-[#F9004D]/10 border border-[#F9004D]/50 rounded-lg p-4">
            <p className="text-zinc-300 text-sm">
              <strong className="text-[#F9004D]">⚠ {t('becomeHost.disclaimer')}</strong>
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="bg-zinc-900/50 border-white/10 p-8 space-y-8">

            {/* Identity Section */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                {t('becomeHost.identity')}
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300 mb-2 block">{t('becomeHost.firstName')} <span className="text-[#F9004D]">*</span></Label>
                  <Input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    placeholder={t('becomeHost.firstNamePlaceholder')}
                    required
                    className="bg-black/20 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300 mb-2 block">{t('becomeHost.lastName')} <span className="text-[#F9004D]">*</span></Label>
                  <Input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    placeholder={t('becomeHost.lastNamePlaceholder')}
                    required
                    className="bg-black/20 border-white/10 text-white"
                  />
                </div>
              </div>

              {/* Champs compte — affichés seulement si pas connecté */}
              {!isAuthenticated ? (
                <div className="space-y-4 pt-2 border-t border-white/10">
                  <p className="text-zinc-400 text-sm">{t('becomeHost.createAccountHint')}</p>
                  {GOOGLE_CLIENT_ID && (
                    <div className="space-y-3">
                      <div ref={googleBtnRef} className="flex justify-center" data-testid="become-host-google" />
                      <div className="flex items-center gap-3">
                        <div className="h-px bg-white/10 flex-1" />
                        <span className="text-xs text-zinc-500">{t('becomeHost.orEmail')}</span>
                        <div className="h-px bg-white/10 flex-1" />
                      </div>
                    </div>
                  )}
                  <div>
                    <Label className="text-zinc-300 mb-2 block">{t('becomeHost.email')} <span className="text-[#F9004D]">*</span></Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder={t('becomeHost.emailPlaceholder')}
                      required
                      className="bg-black/20 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300 mb-2 block">{t('becomeHost.password')} <span className="text-[#F9004D]">*</span></Label>
                    <PasswordInput
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder={t('becomeHost.passwordPlaceholder')}
                      required
                      minLength={6}
                      className="bg-black/20 border-white/10 text-white"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <Label className="text-zinc-300 mb-2 block">{t('becomeHost.contactEmail')}</Label>
                  <Input
                    type="email"
                    value={user?.email || ''}
                    readOnly
                    className="bg-black/10 border-white/5 text-zinc-400 cursor-default"
                  />
                  <p className="text-xs text-zinc-500 mt-1">{t('becomeHost.emailLinked')}</p>
                </div>
              )}
            </div>

            {/* Biography Section */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                {t('becomeHost.personalInfo')}
              </h2>

              <div>
                <Label className="text-zinc-300">{t('becomeHost.bioEn')}</Label>
                <Textarea
                  value={formData.bio_en}
                  onChange={(e) => setFormData({...formData, bio_en: e.target.value})}
                  className="bg-black/20 border-white/10 text-white mt-2"
                  rows={4}
                  placeholder={t('becomeHost.bioEnPlaceholder')}
                />
              </div>

              <div>
                <Label className="text-zinc-300">{t('becomeHost.bioFr')}</Label>
                <Textarea
                  value={formData.bio_fr}
                  onChange={(e) => setFormData({...formData, bio_fr: e.target.value})}
                  className="bg-black/20 border-white/10 text-white mt-2"
                  rows={4}
                  placeholder={t('becomeHost.bioFrPlaceholder')}
                />
              </div>

              <div>
                <Label className="text-zinc-300">{t('becomeHost.bioJp')}</Label>
                <Textarea
                  value={formData.bio_jp}
                  onChange={(e) => setFormData({...formData, bio_jp: e.target.value})}
                  className="bg-black/20 border-white/10 text-white mt-2"
                  rows={4}
                  placeholder={t('becomeHost.bioJpPlaceholder')}
                />
              </div>
            </div>

            {/* Languages + Niveaux combinés */}
            <div className="space-y-4">
              <Label className="text-zinc-300 text-lg">{t('becomeHost.languagesSpoken')} *</Label>
              <p className="text-zinc-500 text-sm">{t('becomeHost.languagesHint')}</p>
              <div className="space-y-3">
                {[
                  { key: 'fr', flag: '🇫🇷', label: 'Français' },
                  { key: 'en', flag: '🇬🇧', label: 'English' },
                  { key: 'jp', flag: '🇯🇵', label: '日本語' },
                  { key: 'es', flag: '🇪🇸', label: 'Español' },
                  { key: 'de', flag: '🇩🇪', label: 'Deutsch' },
                  { key: 'zh', flag: '🇨🇳', label: '中文' },
                ].map(({ key, flag, label }) => {
                  const checked = formData.languages_spoken.includes(key);
                  const lvl = formData.language_levels?.[key] || 0;
                  return (
                    <div key={key} className={`rounded-xl border transition-all p-4 ${checked ? 'border-[#F9004D]/50 bg-[#F9004D]/5' : 'border-white/10 bg-black/10'}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <Checkbox
                          id={`lang-${key}`}
                          checked={checked}
                          onCheckedChange={() => {
                            handleCheckbox('languages_spoken', key);
                            if (!checked && !formData.language_levels?.[key]) {
                              setFormData(prev => ({ ...prev, language_levels: { ...prev.language_levels, [key]: 1 } }));
                            }
                          }}
                          className="border-white/20"
                        />
                        <label htmlFor={`lang-${key}`} className="text-white font-semibold cursor-pointer text-base">
                          {flag} {label}
                        </label>
                      </div>
                      {checked && (
                        <div className="flex items-center gap-2 pl-7">
                          <span className="text-zinc-500 text-xs w-20">{t('becomeHost.levelLabel')}</span>
                          <div className="flex gap-1.5">
                            {[1,2,3,4,5,6,7].map(n => (
                              <button
                                key={n}
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, language_levels: { ...prev.language_levels, [key]: n } }))}
                                className={'w-7 h-7 rounded-full border-2 text-xs font-bold transition-colors ' +
                                  (lvl >= n ? 'bg-[#F9004D] border-[#F9004D] text-white' : 'bg-transparent border-white/20 text-zinc-500 hover:border-white/50')}
                              >
                                {n}
                              </button>
                            ))}
                          </div>
                          <span className="text-zinc-400 text-xs ml-1">
                            {lvl === 0 ? '—' : lvl <= 2 ? t('becomeHost.levels.beginner') : lvl <= 4 ? t('becomeHost.levels.intermediate') : lvl <= 5 ? t('becomeHost.levels.advanced') : lvl === 6 ? t('becomeHost.levels.fluent') : t('becomeHost.levels.native')}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Specialties */}
            <div className="space-y-4">
              <Label className="text-zinc-300 text-lg">{t('becomeHost.specialties')} *</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {specialtyOptions.map(spec => (
                  <div key={spec} className="flex items-center space-x-2">
                    <Checkbox
                      id={`spec-${spec}`}
                      checked={formData.specialties.includes(spec)}
                      onCheckedChange={() => handleCheckbox('specialties', spec)}
                      className="border-white/20"
                    />
                    <label htmlFor={`spec-${spec}`} className="text-zinc-300 cursor-pointer capitalize">
                      {t(`becomeHost.specialtyOptions.${spec}`, { defaultValue: spec })}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Areas */}
            <div className="space-y-3">
              <Label className="text-zinc-300 text-lg">{t('becomeHost.areas')} *</Label>
              <p className="text-zinc-500 text-sm">{t('becomeHost.areasHint', { defaultValue: 'Saisissez les quartiers que vous couvrez, séparés par des virgules.' })}</p>
              <Input
                value={formData.areas_text}
                onChange={(e) => setFormData(prev => ({ ...prev, areas_text: e.target.value }))}
                placeholder="Shibuya, Shinjuku, Odaiba, Kamakura, Nikko..."
                className="bg-black/20 border-white/10 text-white"
              />
            </div>

            {/* Documents */}
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                {t('becomeHost.documents')}
              </h2>
              
              {/* Visa Status */}
              <div className="space-y-2">
                <Label className="text-zinc-300">{t('becomeHost.visaStatus')} *</Label>
                <p className="text-sm text-zinc-500">{t('becomeHost.visaRequired')}</p>
                <select
                  value={formData.visa_status}
                  onChange={(e) => setFormData({...formData, visa_status: e.target.value})}
                  className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#F9004D] focus:ring-1 focus:ring-[#F9004D] transition-all outline-none"
                  required
                >
                  <option value="">Select your visa status...</option>
                  <option value="japanese">{t('becomeHost.visaOptions.japanese')}</option>
                  <option value="spouse">{t('becomeHost.visaOptions.spouse')}</option>
                  <option value="permanent">{t('becomeHost.visaOptions.permanent')}</option>
                  <option value="longterm">{t('becomeHost.visaOptions.longterm')}</option>
                  <option value="working">{t('becomeHost.visaOptions.working')}</option>
                </select>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-zinc-300">{t('becomeHost.idDocument')} *</Label>
                  <div className="relative">
                    <Input
                      type="file"
                      onChange={(e) => handleFileUpload(e, 'id_document_url')}
                      className="bg-black/20 border-white/10 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#F9004D] file:text-white hover:file:bg-[#D00040]"
                      accept="image/*,application/pdf"
                    />
                    {formData.id_document_url && (
                      <CheckCircle className="absolute right-3 top-3 h-5 w-5 text-green-500" />
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">{t('becomeHost.profilePhoto')} *</Label>
                  <div className="relative">
                    <Input
                      type="file"
                      onChange={(e) => handleFileUpload(e, 'profile_photo_url')}
                      className="bg-black/20 border-white/10 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#F9004D] file:text-white hover:file:bg-[#D00040]"
                      accept="image/*"
                    />
                    {formData.profile_photo_url && (
                      <CheckCircle className="absolute right-3 top-3 h-5 w-5 text-green-500" />
                    )}
                  </div>
                  {formData.profile_photo_url && (
                    <img
                      src={formData.profile_photo_url}
                      alt="Aperçu"
                      className="h-20 w-20 rounded-full object-cover border border-white/20"
                    />
                  )}
                </div>
              </div>
            </div>

            {(pendingPhoto || pendingSrc) && (
              <PhotoCropper
                file={pendingPhoto}
                src={pendingSrc || undefined}
                uploading={uploadingPhoto}
                onCancel={() => { setPendingPhoto(null); setPendingSrc(null); }}
                onCropped={uploadCroppedPhoto}
              />
            )}

            {/* Partner Agreement */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                {t('becomeHost.agreement')}
              </h2>
              <div className="bg-zinc-800/50 border border-white/10 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="agreement"
                    checked={formData.agreement_accepted}
                    onCheckedChange={(checked) => setFormData({...formData, agreement_accepted: checked})}
                    className="border-white/20 mt-1"
                    required
                  />
                  <label htmlFor="agreement" className="text-zinc-300 text-sm leading-relaxed cursor-pointer">
                    {t('becomeHost.agreementText')}
                  </label>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#F9004D] hover:bg-[#D00040] text-white rounded-full px-8 py-6 text-lg font-bold shadow-[0_0_15px_rgba(249,0,77,0.4)] hover:shadow-[0_0_25px_rgba(249,0,77,0.6)] transition-all duration-300"
              data-testid="submit-application-button"
            >
              {loading ? t('common.loading') : t('becomeHost.submit')}
            </Button>
          </Card>
        </form>
      </div>
    </div>
  );
}